import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Upload, X, Trash2, Download } from "lucide-react";
import { 
  SubscriptionDashboard, 
  SubscriptionTable, 
  SubscriptionModal, 
  ImportModal,
  LIKELINESS_OPTIONS 
} from "@/components/subscriptions";

const Subscriptions = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [likelinessFilter, setLikelinessFilter] = useState("YES_MIGHT_NOT");
  const [sortByRenewal, setSortByRenewal] = useState("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "", likeliness: "PENDING", signup_date: "", initial_price: "", initial_paid: false, initial_notes: "", renewals: [], notes: ""
  });
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  
  // Confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  const token = localStorage.getItem("token");

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const fetchSubscriptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, setUser, navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== "manager") {
      navigate("/leads");
      return;
    }
    fetchSubscriptions();
    fetchDashboard();
  }, [user, navigate, fetchSubscriptions, fetchDashboard]);

  // Clear selection when subscriptions change
  useEffect(() => {
    setSelectedIds([]);
  }, [subscriptions]);

  const getLatestRenewal = (sub) => {
    const renewals = sub.renewals || [];
    if (renewals.length > 0) return renewals[renewals.length - 1];
    if (sub.renewal_date) return { renewal_date: sub.renewal_date, renewal_price: sub.renewal_price, paid: sub.renewal_paid, likeliness: sub.likeliness };
    return null;
  };

  const getCurrentLikeliness = (sub) => {
    const latest = getLatestRenewal(sub);
    if (latest && latest.likeliness) return latest.likeliness;
    return sub.likeliness || "YES";
  };

  const getRenewalDateForSort = (sub) => {
    const latest = getLatestRenewal(sub);
    if (latest && latest.renewal_date) return new Date(latest.renewal_date).getTime();
    return null;
  };

  const filteredSubscriptions = subscriptions
    .filter(sub => {
      const matchesSearch = sub.business_name?.toLowerCase().includes(search.toLowerCase());
      const currentLikeliness = getCurrentLikeliness(sub);
      
      let matchesLikeliness = false;
      if (likelinessFilter === "all") {
        matchesLikeliness = true;
      } else if (likelinessFilter === "YES_MIGHT_NOT") {
        matchesLikeliness = ["YES", "MIGHT", "NOT"].includes(currentLikeliness);
      } else if (likelinessFilter === "YES_MIGHT") {
        matchesLikeliness = ["YES", "MIGHT"].includes(currentLikeliness);
      } else if (likelinessFilter === "MIGHT_NOT") {
        matchesLikeliness = ["MIGHT", "NOT"].includes(currentLikeliness);
      } else {
        matchesLikeliness = currentLikeliness === likelinessFilter;
      }
      
      return matchesSearch && matchesLikeliness;
    })
    .sort((a, b) => {
      if (!sortByRenewal) return 0;
      const dateA = getRenewalDateForSort(a);
      const dateB = getRenewalDateForSort(b);
      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return 1;
      if (dateB === null) return -1;
      return sortByRenewal === "asc" ? dateA - dateB : dateB - dateA;
    });

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredSubscriptions.map(sub => sub.subscription_id));
    } else {
      setSelectedIds([]);
    }
    setLastSelectedIndex(null);
  };

  const handleSelectSubscription = (subscriptionId, checked, index, event) => {
    // Shift+click for range selection
    if (event?.shiftKey && lastSelectedIndex !== null && lastSelectedIndex !== index) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeIds = filteredSubscriptions.slice(start, end + 1).map(sub => sub.subscription_id);
      
      // Add all subscriptions in range to selection (union with existing)
      setSelectedIds(prev => {
        const newSet = new Set([...prev, ...rangeIds]);
        return Array.from(newSet);
      });
    } else {
      // Normal click - toggle single checkbox
      if (checked) {
        setSelectedIds(prev => [...prev, subscriptionId]);
      } else {
        setSelectedIds(prev => prev.filter(id => id !== subscriptionId));
      }
    }
    
    // Always update last selected index on any click
    setLastSelectedIndex(index);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await axios.delete(`${API}/subscriptions/bulk`, {
        data: { subscription_ids: selectedIds },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${selectedIds.length} subscriptions`);
      setSelectedIds([]);
      fetchSubscriptions();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete subscriptions");
    }
  };

  const confirmBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteConfirm(true);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscriptions_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Subscriptions exported");
    } catch (error) {
      toast.error("Failed to export subscriptions");
    }
  };

  const isAllSelected = filteredSubscriptions.length > 0 && selectedIds.length === filteredSubscriptions.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < filteredSubscriptions.length;

  const handleOpenModal = (subscription = null) => {
    if (subscription) {
      setSelectedSubscription(subscription);
      let renewals = subscription.renewals || [];
      if (!renewals.length && subscription.renewal_date) {
        renewals = [{ renewal_date: subscription.renewal_date, renewal_price: subscription.renewal_price, paid: subscription.renewal_paid || false, likeliness: subscription.likeliness || "YES", notes: "" }];
      }
      setFormData({
        business_name: subscription.business_name || "",
        likeliness: subscription.likeliness || "YES",
        signup_date: subscription.signup_date ? subscription.signup_date.split('T')[0] : "",
        initial_price: subscription.initial_price || "",
        initial_paid: subscription.initial_paid || false,
        initial_notes: subscription.initial_notes || "",
        renewals: renewals.map(r => ({ renewal_date: r.renewal_date ? r.renewal_date.split('T')[0] : "", renewal_price: r.renewal_price || "", paid: r.paid || false, likeliness: r.likeliness || "YES", notes: r.notes || "" })),
        notes: subscription.notes || ""
      });
    } else {
      setSelectedSubscription(null);
      setFormData({ business_name: "", likeliness: "PENDING", signup_date: "", initial_price: "", initial_paid: false, initial_notes: "", renewals: [], notes: "" });
    }
    setIsModalOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!formData.business_name.trim()) { toast.error("Business name is required"); return; }
    try {
      const payload = {
        ...formData,
        initial_price: formData.initial_price ? parseFloat(formData.initial_price) : null,
        signup_date: formData.signup_date || null,
        renewals: formData.renewals.map(r => ({ renewal_date: r.renewal_date || null, renewal_price: r.renewal_price ? parseFloat(r.renewal_price) : null, paid: r.paid || false, likeliness: r.likeliness || "YES", notes: r.notes || "" })).filter(r => r.renewal_date)
      };
      if (selectedSubscription) {
        await axios.put(`${API}/subscriptions/${selectedSubscription.subscription_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Subscription updated");
      } else {
        await axios.post(`${API}/subscriptions`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Subscription created");
      }
      setIsModalOpen(false);
      fetchSubscriptions();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleAutoSaveSubscription = async (updatedFormData) => {
    if (!selectedSubscription) return;
    try {
      const payload = {
        ...updatedFormData,
        initial_price: updatedFormData.initial_price ? parseFloat(updatedFormData.initial_price) : null,
        signup_date: updatedFormData.signup_date || null,
        renewals: updatedFormData.renewals.map(r => ({ renewal_date: r.renewal_date || null, renewal_price: r.renewal_price ? parseFloat(r.renewal_price) : null, paid: r.paid || false, likeliness: r.likeliness || "YES", notes: r.notes || "" })).filter(r => r.renewal_date)
      };
      await axios.put(`${API}/subscriptions/${selectedSubscription.subscription_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment status saved");
      fetchSubscriptions();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to auto-save");
    }
  };

  const handleDeleteSubscription = async (id) => {
    try {
      await axios.delete(`${API}/subscriptions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchSubscriptions();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const confirmDeleteSubscription = (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const handleImportFile = async (file) => {
    const name = (file?.name || "").toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) { toast.error("Please upload an Excel or CSV file"); return; }
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/subscriptions/import`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${response.data.imported} subscriptions`);
      setIsImportModalOpen(false);
      fetchSubscriptions();
      fetchDashboard();
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const toggleRenewalSort = () => {
    if (sortByRenewal === null) setSortByRenewal("asc");
    else if (sortByRenewal === "asc") setSortByRenewal("desc");
    else setSortByRenewal(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="subscriptions-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      <div className="flex-1 p-8 ml-64">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Subscriptions</h1>
            <p className="text-slate-500 mt-1">Manage customer subscriptions and renewals</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            <Button variant="outline" onClick={() => setIsImportModalOpen(true)} data-testid="import-btn">
              <Upload className="w-4 h-4 mr-2" />Import
            </Button>
            <Button onClick={() => handleOpenModal()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-subscription-btn">
              <Plus className="w-4 h-4 mr-2" />Add Subscription
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between animate-fade-in">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-indigo-700">
                {selectedIds.length} subscription{selectedIds.length > 1 ? 's' : ''} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds([])}
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={confirmBulkDelete}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          </div>
        )}

        <SubscriptionDashboard dashboard={dashboard} />

        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search businesses..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="pl-10 pr-10"
              data-testid="subscription-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="clear-search-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={likelinessFilter} onValueChange={setLikelinessFilter}>
            <SelectTrigger className="w-48" data-testid="likeliness-filter">
              <SelectValue placeholder="Filter by likeliness" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="YES_MIGHT_NOT">Yes / Might / Not</SelectItem>
              <SelectItem value="YES_MIGHT">Yes / Might</SelectItem>
              <SelectItem value="MIGHT_NOT">Might / Not</SelectItem>
              <SelectItem value="all">All Likeliness</SelectItem>
              {LIKELINESS_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <SubscriptionTable 
          subscriptions={filteredSubscriptions}
          sortByRenewal={sortByRenewal}
          onSortToggle={toggleRenewalSort}
          onEdit={handleOpenModal}
          onDelete={confirmDeleteSubscription}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectSubscription={handleSelectSubscription}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
        />

        <SubscriptionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          formData={formData}
          setFormData={setFormData}
          selectedSubscription={selectedSubscription}
          onSave={handleSaveSubscription}
          onAutoSave={handleAutoSaveSubscription}
        />

        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          importLoading={importLoading}
          onImport={handleImportFile}
        />

        {/* Delete Subscription Confirmation */}
        <ConfirmDialog
          open={deleteConfirm.open}
          onOpenChange={(open) => setDeleteConfirm({ open, id: null })}
          title="Delete Subscription?"
          description="Are you sure you want to delete this subscription? This action cannot be undone."
          confirmText="Delete"
          variant="destructive"
          onConfirm={() => handleDeleteSubscription(deleteConfirm.id)}
        />

        {/* Bulk Delete Confirmation */}
        <ConfirmDialog
          open={bulkDeleteConfirm}
          onOpenChange={setBulkDeleteConfirm}
          title={`Delete ${selectedIds.length} Subscription${selectedIds.length > 1 ? 's' : ''}?`}
          description={`Are you sure you want to delete ${selectedIds.length} selected subscription${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
          confirmText="Delete"
          variant="destructive"
          onConfirm={handleBulkDelete}
        />
      </div>
    </div>
  );
};

export default Subscriptions;
